import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faBuilding, faUser, faPhone, faEnvelope, faSpinner, 
    faAngleLeft, faAngleRight, faCalendarAlt, faPlus, faMapMarkerAlt, faIdCard, faLock,
    faFileContract, faCloudUploadAlt, faTrash, faFileAlt, faTimes
} from "@fortawesome/free-solid-svg-icons";
import PageHeadingSection from "../../../../common/pageheading/PageHeadingSection";
import CustomSelect from "../../../../common/formfields/CustomSelect";
import CustomMultiSelect from "../../../../common/formfields/CustomMultiSelect";
import Breadcrumbs from "../../../../common/BreadCrum/BreadCrum";
import apiClient from "../../../../../services/apiClient";

const VendorOnboardingForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user } = useSelector((state) => state.auth);
    
    const roleName = (user?.RoleName || user?.roleName || user?.role || user?.Role || localStorage.getItem('roleName') || "").toLowerCase();
    const isSiteAdmin = roleName.includes('site admin') || roleName.includes('super');
    const assignedSiteId = user?.siteId || 2; 
    const assignedUnitId = isSiteAdmin ? null : (user?.branchorunitID || 2);

    const vendorId = searchParams.get('vendorId') || location.state?.vendorId || location.state?.vendorData?.vendorId || location.state?.vendorData?.id;
    const isEditMode = Boolean(vendorId);

    const [formData, setFormData] = useState({
        vendorName: "",
        contactPerson: "",
        mobileNumber: "",
        email: "",
        address: "",
        contractStartDate: "",
        contractEndDate: "",
        gstNumber: "",
        panNumber: "",
        categoryName: "",
        subCategoryName: [], // Array of option objects for CustomMultiSelect
        agreementDocUrl: "",
        isApprovalRequired: true
    });

    const [docFileName, setDocFileName] = useState("");
    const [docFileSize, setDocFileSize] = useState("");

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [categories, setCategories] = useState([]);
    const [fetchingCategories, setFetchingCategories] = useState(false);

    const [categoryModal, setCategoryModal] = useState({ isOpen: false, newCategoryName: "" });
    const [subCategoryModal, setSubCategoryModal] = useState({ isOpen: false, newSubCategoryName: "" });

    const fetchCategories = useCallback(async (customParams = {}) => {
        setFetchingCategories(true);
        try {
            // Build dynamic query params flexibly based on what is provided
            const params = {
                ...(assignedSiteId ? { siteId: assignedSiteId } : {}),
                ...customParams
            };

            // Remove undefined, null, or empty string values
            const cleanParams = Object.fromEntries(
                Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== "")
            );

            const response = await apiClient.get('/vendor-management/vendor/GetCategories', {
                params: cleanParams
            });

            if (response.data?.isSuccess && response.data?.data) {
                const formatted = response.data.data.map(cat => ({
                    categoryId: cat.categoryId,
                    label: cat.categoryName,
                    value: cat.categoryName,
                    siteId: cat.siteId,
                    subCategories: (cat.subCategories || []).map(sub => ({
                        subCategoryId: sub.subCategoryId,
                        label: sub.subCategoryName,
                        value: sub.subCategoryName
                    }))
                }));
                setCategories(formatted);
            }
        } catch (error) {
            console.error("Failed to fetch categories", error);
            toast.error("Failed to fetch categories");
        } finally {
            setFetchingCategories(false);
        }
    }, [assignedSiteId]);

    useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    // Fetch vendor details if in Edit Mode
    useEffect(() => {
        const fetchVendorDetails = async () => {
            if (!vendorId) return;
            setLoading(true);
            try {
                const response = await apiClient.get('/vendor-management/vendor/GetVendors', {
                    params: {
                        siteId: assignedSiteId,
                        vendorId: vendorId
                    }
                });

                if (response.data?.isSuccess && response.data?.data) {
                    const vendor = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
                    if (vendor) {
                        setFormData({
                            vendorName: vendor.vendorName || "",
                            contactPerson: vendor.contactPerson || "",
                            mobileNumber: vendor.mobileNumber || "",
                            email: vendor.email || "",
                            address: vendor.address || "",
                            contractStartDate: vendor.contractStartDate ? vendor.contractStartDate.split('T')[0] : "",
                            contractEndDate: vendor.contractEndDate ? vendor.contractEndDate.split('T')[0] : "",
                            gstNumber: vendor.gstNumber || "",
                            panNumber: vendor.panNumber || "",
                            categoryName: vendor.categoryName || "",
                            subCategoryName: (vendor.subCategories || []).map(sc => typeof sc === 'string' ? { label: sc, value: sc } : sc),
                            agreementDocUrl: vendor.agreementDocUrl || "",
                            isApprovalRequired: vendor.isApprovalRequired !== undefined ? vendor.isApprovalRequired : true
                        });
                        if (vendor.agreementDocUrl) {
                            setDocFileName("Vendor_Agreement_Document");
                        }
                    }
                }
            } catch (error) {
                console.error("Failed to fetch vendor details:", error);
                toast.error("Failed to load vendor details for editing");
            } finally {
                setLoading(false);
            }
        };

        fetchVendorDetails();
    }, [vendorId, assignedSiteId]);

    const validate = () => {
        let newErrors = {};
        if (!formData.vendorName) newErrors.vendorName = "Vendor Company Name is required";
        if (!formData.contactPerson) newErrors.contactPerson = "Contact Person is required";
        if (!formData.mobileNumber) newErrors.mobileNumber = "Mobile Number is required";
        else if (!/^[0-9]{10}$/.test(formData.mobileNumber)) newErrors.mobileNumber = "Invalid mobile number";
        if (!formData.email) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = "Invalid email format";
        if (!formData.categoryName) newErrors.categoryName = "Category is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
        if (errors[id]) {
            setErrors(prev => ({ ...prev, [id]: null }));
        }
    };

    const handleAddCategory = () => {
        if (!categoryModal.newCategoryName.trim()) {
            toast.error("Category name cannot be empty");
            return;
        }
        const newCat = {
            label: categoryModal.newCategoryName,
            value: categoryModal.newCategoryName,
            subCategories: []
        };
        setCategories([...categories, newCat]);
        setFormData(prev => ({ ...prev, categoryName: newCat.value, subCategoryName: [] }));
        setCategoryModal({ isOpen: false, newCategoryName: "" });
        toast.success("Category added successfully");
    };

    const handleAddSubCategory = () => {
        if (!formData.categoryName) {
            toast.error("Please select a category first");
            return;
        }
        if (!subCategoryModal.newSubCategoryName.trim()) {
            toast.error("Subcategory name cannot be empty");
            return;
        }
        
        const newSubCat = { label: subCategoryModal.newSubCategoryName, value: subCategoryModal.newSubCategoryName };
        
        setCategories(categories.map(cat => {
            if (cat.value === formData.categoryName) {
                return { ...cat, subCategories: [...cat.subCategories, newSubCat] };
            }
            return cat;
        }));
        
        setFormData(prev => ({ ...prev, subCategoryName: [...prev.subCategoryName, newSubCat] }));
        setSubCategoryModal({ isOpen: false, newSubCategoryName: "" });
        toast.success("Subcategory added successfully");
    };

    const handleDocUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 10 * 1024 * 1024) {
            toast.error("File size must be under 10MB");
            return;
        }

        setDocFileName(file.name);
        setDocFileSize((file.size / (1024 * 1024)).toFixed(2) + " MB");

        const reader = new FileReader();
        reader.onloadend = () => {
            setFormData(prev => ({ ...prev, agreementDocUrl: reader.result }));
        };
        reader.readAsDataURL(file);
    };

    const handleRemoveDoc = () => {
        setDocFileName("");
        setDocFileSize("");
        setFormData(prev => ({ ...prev, agreementDocUrl: "" }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Please fill all required fields correctly.");
            return;
        }

        setLoading(true);
        
        const selectedCategoryObj = categories.find(c => c.value === formData.categoryName);

        if (isEditMode) {
            const updatePayload = {
                vendorId: Number(vendorId),
                siteId: assignedSiteId,
                categoryId: selectedCategoryObj?.categoryId || null,
                vendorName: formData.vendorName,
                contactPerson: formData.contactPerson,
                mobileNumber: formData.mobileNumber,
                email: formData.email,
                address: formData.address,
                contractStartDate: formData.contractStartDate || null,
                contractEndDate: formData.contractEndDate || null,
                gstNumber: formData.gstNumber,
                panNumber: formData.panNumber,
                agreementDocUrl: formData.agreementDocUrl || "",
                status: "Active",
                modifiedBy: roleName || "SiteAdmin",
                subCategoryName: formData.subCategoryName.map(sc => typeof sc === 'object' ? sc.value : sc),
                isApprovalRequired: formData.isApprovalRequired
            };

            try {
                await apiClient.put(`/vendor-management/vendor/UpdateVendor?vendorId=${vendorId}`, updatePayload);
                toast.success("Vendor updated successfully!");
                navigate("/zentres/workforce-management/vendor");
            } catch (error) {
                console.error("Error updating vendor:", error);
                toast.error("Failed to update vendor. Please try again.");
            } finally {
                setLoading(false);
            }
        } else {
            const createPayload = {
                siteId: assignedSiteId,
                unitId: assignedUnitId || null,
                categoryId: selectedCategoryObj?.categoryId || null,
                categoryName: formData.categoryName,
                subCategoryName: formData.subCategoryName.map(sc => typeof sc === 'object' ? sc.value : sc),
                vendorName: formData.vendorName,
                contactPerson: formData.contactPerson,
                mobileNumber: formData.mobileNumber,
                email: formData.email,
                address: formData.address,
                contractStartDate: formData.contractStartDate || null,
                contractEndDate: formData.contractEndDate || null,
                gstNumber: formData.gstNumber,
                panNumber: formData.panNumber,
                agreementDocUrl: formData.agreementDocUrl || "",
                status: "Active",
                createdBy: roleName || "SiteAdmin",
                password: formData.password || "",
                personTypeId: 8250,
                isApprovalRequired: formData.isApprovalRequired
            };

            try {
                await apiClient.post('/vendor-management/vendor/CreateVendor', createPayload);
                toast.success("Vendor onboarded successfully!");
                navigate("/zentres/workforce-management/vendor");
            } catch (error) {
                console.error("Error onboarding vendor:", error);
                toast.error("Failed to onboard vendor. Please try again.");
            } finally {
                setLoading(false);
            }
        }
    };

    const selectedCategoryObj = categories.find(c => c.value === formData.categoryName);
    const subCategoryOptions = selectedCategoryObj ? selectedCategoryObj.subCategories : [];

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 pb-12 min-h-0">
            <Breadcrumbs items={["Workforce Management", "Vendor", isEditMode ? "Update Vendor" : "Onboard Vendor"]} />

            <div className="mb-6 mt-4">
                <PageHeadingSection
                    Navbar={true}
                    mainHeading={isEditMode ? "Update Vendor" : "Onboard New Vendor"}
                    subHeading={isEditMode ? "Modify vendor details, service categories, and contracts." : "Register a new vendor and automatically send login credentials."}
                />
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col">
                <form onSubmit={handleSubmit} className="p-6 md:p-8 flex flex-col">
                    <div className="flex flex-col gap-8 max-w-3xl">
                        
                        {/* Categories Section - Reverted styling to match old form but keeping grids */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Category Name <span className="text-red-500">*</span>
                                </label>
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <CustomSelect
                                            options={[{label: "Select Category", value: ""}, ...categories.map(c => ({label: c.label, value: c.value}))]}
                                            value={formData.categoryName ? {label: formData.categoryName, value: formData.categoryName} : null}
                                            onChange={(val) => {
                                                setFormData(prev => ({ ...prev, categoryName: val.value, subCategoryName: [] }));
                                                if(errors.categoryName) setErrors(prev => ({ ...prev, categoryName: null }));
                                            }}
                                            placeholder={fetchingCategories ? "Loading..." : "Select Category"}
                                            error={errors.categoryName}
                                        />
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => setCategoryModal({ isOpen: true, newCategoryName: "" })}
                                        className="px-3 py-2 bg-blue-50 text-blue-600 rounded-md text-xs font-semibold hover:bg-blue-100 transition-colors shrink-0 border border-blue-100"
                                        title="Add New Category"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                </div>
                                {errors.categoryName && <p className="text-red-500 text-[10px] mt-1">{errors.categoryName}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Subcategories
                                </label>
                                <div className="flex gap-2">
                                    <div className="flex-1">
                                        <CustomMultiSelect
                                            options={subCategoryOptions}
                                            value={formData.subCategoryName}
                                            onChange={(val) => setFormData(prev => ({ ...prev, subCategoryName: val }))}
                                            placeholder="Select Subcategories"
                                            isDisabled={!formData.categoryName}
                                        />
                                    </div>
                                    <button 
                                        type="button"
                                        onClick={() => setSubCategoryModal({ isOpen: true, newSubCategoryName: "" })}
                                        disabled={!formData.categoryName}
                                        className={`px-3 py-2 rounded-md text-xs font-semibold transition-colors shrink-0 border ${!formData.categoryName ? 'bg-gray-50 text-gray-400 border-gray-100 cursor-not-allowed' : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border-blue-100'}`}
                                        title="Add New Subcategory"
                                    >
                                        <FontAwesomeIcon icon={faPlus} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Basic Info Section */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="vendorName">
                                    Vendor / Company Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faBuilding} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.vendorName ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} 
                                        type="text" id="vendorName" value={formData.vendorName} onChange={handleInputChange} 
                                        placeholder="e.g. Acme Services" 
                                    />
                                </div>
                                {errors.vendorName && <p className="text-red-500 text-[10px] mt-1">{errors.vendorName}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="contactPerson">
                                    Contact Person <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faUser} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.contactPerson ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} 
                                        type="text" id="contactPerson" value={formData.contactPerson} onChange={handleInputChange} 
                                        placeholder="e.g. Rahul Sharma" 
                                    />
                                </div>
                                {errors.contactPerson && <p className="text-red-500 text-[10px] mt-1">{errors.contactPerson}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="mobileNumber">
                                    Primary Mobile <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faPhone} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.mobileNumber ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} 
                                        type="text" id="mobileNumber" value={formData.mobileNumber} onChange={handleInputChange} 
                                        placeholder="10-digit mobile number" maxLength={10}
                                    />
                                </div>
                                {errors.mobileNumber && <p className="text-red-500 text-[10px] mt-1">{errors.mobileNumber}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="email">
                                    Primary Email <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faEnvelope} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.email ? 'border-red-500' : 'border-gray-200 dark:border-bordercolor hover:border-gray-400'}`} 
                                        type="email" id="email" value={formData.email} onChange={handleInputChange} 
                                        placeholder="vendor@company.com" 
                                    />
                                </div>
                                {errors.email && <p className="text-red-500 text-[10px] mt-1">{errors.email}</p>}
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="address">
                                    Company Address
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 pt-2.5 pointer-events-none">
                                        <FontAwesomeIcon icon={faMapMarkerAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <textarea 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 dark:border-bordercolor hover:border-gray-400`} 
                                        id="address" rows="2" value={formData.address} onChange={handleInputChange} 
                                        placeholder="123 Business Park, Mumbai" 
                                    ></textarea>
                                </div>
                            </div>
                        </div>

                        {/* Contract & Tax Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="contractStartDate">
                                    Contract Start Date
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 dark:border-bordercolor hover:border-gray-400`} 
                                        type="date" id="contractStartDate" value={formData.contractStartDate} onChange={handleInputChange} 
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="contractEndDate">
                                    Contract End Date
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faCalendarAlt} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 dark:border-bordercolor hover:border-gray-400`} 
                                        type="date" id="contractEndDate" value={formData.contractEndDate} onChange={handleInputChange} 
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="gstNumber">
                                    GST Number
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faIdCard} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 dark:border-bordercolor hover:border-gray-400`} 
                                        type="text" id="gstNumber" value={formData.gstNumber} onChange={handleInputChange} 
                                        placeholder="e.g. 27AADCC1234E1Z5" 
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="panNumber">
                                    PAN Number
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FontAwesomeIcon icon={faIdCard} className="text-gray-400 text-xs" />
                                    </div>
                                    <input 
                                        className={`w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-transparent dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all border-gray-200 dark:border-bordercolor hover:border-gray-400`} 
                                        type="text" id="panNumber" value={formData.panNumber} onChange={handleInputChange} 
                                        placeholder="e.g. AADCC1234E" 
                                    />
                                </div>
                            </div>

                            <div className="md:col-span-2 lg:col-span-2">
                                <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">
                                    Vendor Agreement Document
                                </label>
                                {formData.agreementDocUrl ? (
                                    <div className="flex items-center justify-between p-2.5 border border-emerald-200 bg-emerald-50/50 rounded-lg">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-600 flex items-center justify-center text-sm">
                                                <FontAwesomeIcon icon={faFileContract} />
                                            </div>
                                            <div>
                                                <p className="text-xs font-semibold text-gray-800 truncate max-w-[180px] sm:max-w-xs">{docFileName || "Agreement_Document.pdf"}</p>
                                                <p className="text-[10px] text-gray-500">{docFileSize || "Document attached"}</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleRemoveDoc}
                                            className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                                            title="Remove Document"
                                        >
                                            <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                        </button>
                                    </div>
                                ) : (
                                    <label className="relative border border-dashed border-gray-200 hover:border-blue-400 dark:border-bordercolor rounded-lg p-2.5 flex items-center justify-center gap-2 cursor-pointer transition-all duration-200 group bg-gray-50/50 hover:bg-blue-50/30">
                                        <input
                                            type="file"
                                            accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                                            onChange={handleDocUpload}
                                            className="hidden"
                                        />
                                        <div className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center text-xs group-hover:scale-110 transition-transform">
                                            <FontAwesomeIcon icon={faCloudUploadAlt} />
                                        </div>
                                        <div className="text-left">
                                            <p className="text-xs font-semibold text-gray-700">
                                                Upload Agreement <span className="text-gray-400 font-normal text-[10px]">(PDF, DOC, PNG, JPG up to 10MB)</span>
                                            </p>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* Policy Settings Section */}
                        {isSiteAdmin && (
                            <div className="pt-4 border-t border-gray-100 dark:border-bordercolor">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            className="sr-only"
                                            checked={formData.isApprovalRequired}
                                            onChange={(e) => setFormData(prev => ({ ...prev, isApprovalRequired: e.target.checked }))}
                                        />
                                        <div className={`block w-10 h-6 rounded-full transition-colors ${formData.isApprovalRequired ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                                        <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.isApprovalRequired ? 'transform translate-x-4' : ''}`}></div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-bold text-gray-700 dark:text-textsecondary">Require Staff Approval (Policy Engine)</div>
                                        <div className="text-[10px] text-gray-500">If enabled, staff members from this vendor will require approval when assigned to duty, unless overridden.</div>
                                    </div>
                                </label>
                            </div>
                        )}

                    </div>

                    <div className="flex justify-between mt-10 border-t pt-4 shrink-0 max-w-3xl">
                        <button
                            type="button"
                            onClick={() => navigate("/zentres/workforce-management/vendor")}
                            className="px-3 py-2 border border-gray-200 rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            <FontAwesomeIcon icon={faAngleLeft} />
                            Cancel
                        </button>
                        <div className="flex gap-3">
                            <button
                                type="submit"
                                disabled={loading}
                                className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                                style={{ backgroundColor: "var(--color-secondaryaccent)", color: "var(--color-primaryaccent)" }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = "var(--color-primaryaccent)";
                                    e.currentTarget.style.color = '#FFFFFF';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = "var(--color-secondaryaccent)";
                                    e.currentTarget.style.color = "var(--color-primaryaccent)";
                                }}
                            >
                                {loading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEditMode ? 'Update Vendor' : 'Onboard Vendor')}
                                <FontAwesomeIcon icon={faAngleRight} />
                            </button>
                        </div>
                    </div>
                </form>
            </div>

            {/* Modal for Adding New Category */}
            {categoryModal.isOpen && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full mx-4 border border-gray-100">
                        <h2 className="text-lg font-bold mb-4 text-gray-800">Add New Category</h2>
                        <input
                            type="text"
                            value={categoryModal.newCategoryName}
                            onChange={(e) => setCategoryModal({ ...categoryModal, newCategoryName: e.target.value })}
                            placeholder="Enter category name"
                            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 mb-4"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button 
                                onClick={() => setCategoryModal({ isOpen: false, newCategoryName: "" })}
                                className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleAddCategory}
                                className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                            >
                                Add Category
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal for Adding New Subcategory */}
            {subCategoryModal.isOpen && (
                <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
                    <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full mx-4 border border-gray-100">
                        <h2 className="text-lg font-bold mb-1 text-gray-800">Add Subcategory</h2>
                        <p className="text-xs text-gray-500 mb-4">Adding to: <span className="font-semibold">{typeof formData.categoryName === 'string' ? formData.categoryName : ''}</span></p>
                        <input
                            type="text"
                            value={subCategoryModal.newSubCategoryName}
                            onChange={(e) => setSubCategoryModal({ ...subCategoryModal, newSubCategoryName: e.target.value })}
                            placeholder="Enter subcategory name"
                            className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 mb-4"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <button 
                                onClick={() => setSubCategoryModal({ isOpen: false, newSubCategoryName: "" })}
                                className="px-3 py-1.5 text-xs font-semibold text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
                            >
                                Cancel
                            </button>
                            <button 
                                onClick={handleAddSubCategory}
                                className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
                            >
                                Add Subcategory
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default VendorOnboardingForm;
